import { Badge, Button } from '@mui/material';
import useRoles from 'common/hooks/useRoles';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import CancelBulkLineItemDialog from './CancelBulkLineItemDialog';
import { usePrescriptionReviewSelection } from './PrescriptionReviewSelectionContext';

const BulkCancelPrescriptionButton = ({ onSuccess }) => {
  const { cancelBulkLineItems } = useRoles();
  const { selectedItemIds } = usePrescriptionReviewSelection();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedLineItems, setSelectedLineItems] = useState([]);

  const { pages, currentPage } = useSelector(({ workQueue }) => workQueue.prescriptionReview);
  const prescriptions = useMemo(() => pages[currentPage] || [], [pages, currentPage]);

  useEffect(() => {
    if (isEmpty(selectedItemIds) || isEmpty(prescriptions)) {
      setSelectedLineItems([]);
      return;
    }

    const selectedItems = prescriptions
      .filter(
        (prescription) =>
          prescription.lineItem?.requestLineItemId &&
          selectedItemIds.includes(prescription.lineItem.requestLineItemId)
      )
      .map((prescription) => ({
        lineItemId: prescription.lineItem.requestLineItemId,
        orderId: prescription.orderId,
        patientName: prescription.patient?.patientName,
        medicationName: prescription.drugName || prescription.prescriptionData?.drug,
        isMarkedForFillLater: prescription.isMarkedForFillLater,
      }));

    setSelectedLineItems(selectedItems);
  }, [selectedItemIds, prescriptions]);

  const handleOpenDialog = () => setIsDialogOpen(true);

  const handleCloseDialog = () => setIsDialogOpen(false);

  const handleSuccess = () => {
    handleCloseDialog();
    if (onSuccess) {
      onSuccess();
    }
  };

  if (!cancelBulkLineItems) {
    return null;
  }

  const itemCount = selectedLineItems.length;
  const isDisabled = itemCount === 0;

  return (
    <>
      <Badge color='error' badgeContent={itemCount} sx={{ mx: 1 }}>
        <Button
          variant='outlined'
          color='error'
          onClick={handleOpenDialog}
          disabled={isDisabled}
          sx={{
            height: 33,
            backgroundColor: (theme) => theme.palette.common.white,
            borderColor: (theme) => theme.palette.error.main,
            color: (theme) => theme.palette.error.main,
            '&:hover': {
              backgroundColor: (theme) => theme.palette.common.white,
              borderColor: (theme) => theme.palette.error.main,
            },
            '&.Mui-disabled': {
              backgroundColor: (theme) => theme.palette.action.disabledBackground,
              borderColor: 'transparent',
              color: (theme) => theme.palette.action.disabled,
            },
          }}
        >
          Cancel Items
        </Button>
      </Badge>

      {isDialogOpen && (
        <CancelBulkLineItemDialog
          open={isDialogOpen}
          onClose={handleCloseDialog}
          lineItems={selectedLineItems}
          onSuccess={handleSuccess}
        />
      )}
    </>
  );
};

BulkCancelPrescriptionButton.propTypes = {
  onSuccess: PropTypes.func,
};

BulkCancelPrescriptionButton.defaultProps = {
  onSuccess: undefined,
};

export default BulkCancelPrescriptionButton;
